import { defineStore } from 'pinia';
import axios from '@/services/axios';
import { SecureStorage } from '@/utils/storage';

const CACHE_KEY = 'app_embassy';

export const useEmbassyStore = defineStore('embassy', {
  state: () => ({
    embassies: [],
    isLoading: false,
    error: null,
    lastFetchedLang: null,
  }),
  actions: {
    _loadFromCache(lang) {
      const cached = SecureStorage.getItem(CACHE_KEY);
      if (cached && Array.isArray(cached.data) && cached.lang === lang) {
        this.embassies = cached.data;
        this.lastFetchedLang = lang;
        return true;
      }
      return false;
    },

    _saveToCache(lang) {
      const minimal = this.embassies.map(e => ({
        id: e.id,
        country: e.country,
        name: e.name,
        photo: e.photo,
        description: e.description,
        ambassador: e.ambassador,
        isFullyLoaded: false, // Never cache as fully loaded
      }));
      SecureStorage.setItem(CACHE_KEY, { data: minimal, lang, time: Date.now() }, SecureStorage.TTL.EMBASSY);
    },

    addOrUpdateEmbassy(embassyData) {
      if (!embassyData || !embassyData.id) return;

      const index = this.embassies.findIndex(e => e.id === embassyData.id);
      const dataToStore = { ...embassyData, isFullyLoaded: false };

      if (index !== -1) {
        if (!this.embassies[index].isFullyLoaded) {
          this.embassies[index] = { ...this.embassies[index], ...dataToStore };
        }
      } else {
        this.embassies.push(dataToStore);
      }
    },

    async fetchEmbassies(lang) {
      // If lang not passed, read from the axios interceptor's i18n
      // The interceptor in services/axios.js already sets Accept-Language from i18n
      if (!lang) lang = 'en';

      // Return cached data only if same language
      if (this.embassies.length > 0 && this.lastFetchedLang === lang) return;

      // Try loading from persistent cache with matching lang
      if (this._loadFromCache(lang)) return;

      this.isLoading = true;
      this.error = null;
      try {
        const response = await axios.get('/embassies/');
        this.embassies = response.data.map(embassy => ({
          ...embassy,
          isFullyLoaded: false,
        }));
        this.lastFetchedLang = lang;
        this._saveToCache(lang);
      } catch (err) {
        this.error = err.response?.data?.detail || err.message || 'An unknown error occurred';
      } finally {
        this.isLoading = false;
      }
    },

    findEmbassyByCountry(countryName) {
      if (!countryName) return null;
      return this.embassies.find(e => e.country?.toLowerCase() === countryName.toLowerCase());
    },

    updateEmbassyDetails(embassyData) {
      const index = this.embassies.findIndex(e => e.id === embassyData.id);
      if (index !== -1) {
        this.embassies[index] = { ...this.embassies[index], ...embassyData, isFullyLoaded: true };
      } else {
        this.embassies.push({ ...embassyData, isFullyLoaded: true });
      }
    },
  },
});
